import { Box, Container, Spinner, Text, useToast } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  // Prevent duplicate execution in React 18 Strict Mode
  const hasRun = React.useRef(false);

  useEffect(() => {
    // Skip if already executed (React 18 Strict Mode runs useEffect twice)
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    const handleCallback = async () => {
      // Supabase returns tokens in URL hash fragments (after #)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      // Also check query params for our custom error handling
      const error = searchParams.get("error");
      
      // Extract tokens from hash
      let accessToken = hashParams.get("access_token");
      let refreshToken = hashParams.get("refresh_token");
      let expiresIn = hashParams.get("expires_in");
      
      // Fallback to query params (for custom backend redirects)
      if (!accessToken) {
        accessToken = searchParams.get("access_token");
        refreshToken = searchParams.get("refresh_token");
        expiresIn = searchParams.get("expires_in");
      }

      // Handle errors
      if (error) {
        let errorMessage = "Authentication failed";
        
        switch (error) {
          case "linkedin_auth_failed":
            errorMessage = "LinkedIn authentication was cancelled or failed";
            break;
          case "google_auth_failed":
            errorMessage = "Google authentication was cancelled or failed";
            break;
          case "access_denied":
            errorMessage = "You denied access";
            break;
          case "server_error":
            errorMessage = "Authentication server error";
            break;
          default:
            errorMessage = "An error occurred during authentication";
        }

        toast({
          title: "Authentication Failed",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        navigate("/login");
        return;
      }

      // Handle successful authentication
      if (accessToken) {
        // Store tokens
        localStorage.setItem("token", accessToken);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }

        // Get landing flag to determine if first-time or returning user
        let isFirstTimeUser = false;
        try {
          const API_BASE = process.env.REACT_APP_API_URL || "https://localhost:7211";
          const flagRes = await fetch(`${API_BASE}/api/landing/get`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (flagRes.ok) {
            const flagData = await flagRes.json();
            const landingFlag = flagData.landingFlag ?? 0;
            isFirstTimeUser = landingFlag === 0; // First-time users have flag 0

            // Show appropriate welcome message
            toast({
              title: "Login successful",
              description: isFirstTimeUser ? "Welcome! Let's get started." : "Welcome back!",
              status: "success",
              duration: 2000,
              isClosable: true,
            });

            // Navigate based on landing flag
            switch (landingFlag) {
              case 0:
                navigate("/me");
                break;
              case 1:
              case 2:
              case 3:
                navigate("/dashboard");
                break;
              case 4:
              case 5:
                navigate("/me/profile/resume-preview");
                break;
              default:
                navigate("/me");
                break;
            }
          } else {
            // Fallback if landing flag fetch fails
            toast({
              title: "Login successful",
              description: "Welcome!",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Failed to get landing flag:", error);
          // Fallback toast
          toast({
            title: "Login successful",
            description: "Welcome!",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
          navigate("/dashboard");
        }
      } else {
        // No tokens and no error - something went wrong
        toast({
          title: "Authentication Error",
          description: "No authentication data received",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        navigate("/login");
      }
    };

    handleCallback();
  }, [searchParams, navigate, toast]);

  return (
    <Container
      display="flex"
      alignItems="center"
      justifyContent="center"
      minH="100vh"
    >
      <Box textAlign="center">
        <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
        <Text fontSize="lg" color="gray.600">
          Completing authentication...
        </Text>
      </Box>
    </Container>
  );
}
